import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface SyncLog {
  id: string;
  user_id: string;
  platform: 'mobile' | 'web';
  action: string;
  metadata: Record<string, any>;
  created_at: string;
}

class RealtimeSyncService {
  private contributionsChannel: RealtimeChannel | null = null;
  private loansChannel: RealtimeChannel | null = null;
  private membersChannel: RealtimeChannel | null = null;
  private groupsChannel: RealtimeChannel | null = null;
  private syncLogsChannel: RealtimeChannel | null = null;
  private isSubscribed = false;
  private connectionState: 'connected' | 'disconnected' | 'connecting' = 'disconnected';
  private onConnectionStateChange?: (state: 'connected' | 'disconnected' | 'connecting') => void;

  // Callbacks
  onContributionAdded?: (contribution: any) => void;
  onContributionUpdated?: (contribution: any) => void;
  onContributionDeleted?: (contributionId: string) => void;
  onLoanAdded?: (loan: any) => void;
  onLoanUpdated?: (loan: any) => void;
  onLoanDeleted?: (loanId: string) => void;
  onMemberAdded?: (member: any) => void;
  onMemberUpdated?: (member: any) => void;
  onMemberDeleted?: (memberId: string) => void;
  onGroupUpdated?: (group: any) => void;
  onGroupDeleted?: (groupId: string) => void;
  onSyncLogReceived?: (log: SyncLog) => void;

  /**
   * Subscribe to real-time updates for a specific group
   * Returns true if subscription was successful
   */
  async subscribeToGroup(groupId: string): Promise<boolean> {
    if (this.isSubscribed && this.connectionState === 'connected') {
      return true;
    }

    this.connectionState = 'connecting';
    this.onConnectionStateChange?.(this.connectionState);

    try {
      // Subscribe to contributions
      this.contributionsChannel = supabase
        .channel(`contributions:${groupId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'contributions',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 New contribution from mobile/web:', payload.new);
            this.onContributionAdded?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'contributions',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Contribution updated:', payload.new);
            this.onContributionUpdated?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'contributions',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Contribution deleted:', payload.old);
            const deletedContribution = payload.old as any;
            if (deletedContribution?.id) {
              this.onContributionDeleted?.(deletedContribution.id);
            }
          }
        );

      // Subscribe to loans
      this.loansChannel = supabase
        .channel(`loans:${groupId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'loans',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 New loan:', payload.new);
            this.onLoanAdded?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'loans',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Loan updated:', payload.new);
            this.onLoanUpdated?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'loans',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Loan deleted:', payload.old);
            const deletedLoan = payload.old as any;
            if (deletedLoan?.id) {
              this.onLoanDeleted?.(deletedLoan.id);
            }
          }
        );

      // Subscribe to groups table changes
      this.groupsChannel = supabase
        .channel(`groups:${groupId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'groups',
            filter: `id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Group updated:', payload.new);
            this.onGroupUpdated?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'groups',
            filter: `id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Group deleted:', payload.old);
            this.onGroupDeleted?.(groupId);
          }
        );

      await this.groupsChannel.subscribe();

      // Subscribe to group members
      this.membersChannel = supabase
        .channel(`members:${groupId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'group_members',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 New member:', payload.new);
            this.onMemberAdded?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'group_members',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Member updated:', payload.new);
            this.onMemberUpdated?.(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'group_members',
            filter: `group_id=eq.${groupId}`,
          },
          (payload) => {
            console.log('📥 Member deleted:', payload.old);
            const deletedMember = payload.old as any;
            if (deletedMember?.user_id) {
              this.onMemberDeleted?.(deletedMember.user_id);
            }
          }
        );

      // Subscribe to sync logs (for tracking mobile app activity)
      this.syncLogsChannel = supabase
        .channel('sync_logs')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'sync_logs',
          },
          (payload) => {
            console.log('📥 Sync log:', payload.new);
            this.onSyncLogReceived?.(payload.new as SyncLog);
          }
        );

      // Subscribe to all channels and check their connection state
      const subscribePromises = [
        this.contributionsChannel.subscribe(),
        this.loansChannel.subscribe(),
        this.membersChannel.subscribe(),
        this.groupsChannel.subscribe(),
        this.syncLogsChannel.subscribe(),
      ];

      await Promise.all(subscribePromises);

      // Set up connection state listeners for all channels
      const handleSystemEvent = (payload: any) => {
        if (payload.status === 'SUBSCRIBED') {
          this.connectionState = 'connected';
          this.onConnectionStateChange?.(this.connectionState);
        } else if (payload.status === 'CHANNEL_ERROR' || payload.status === 'TIMED_OUT' || payload.status === 'CLOSED') {
          this.connectionState = 'disconnected';
          this.onConnectionStateChange?.(this.connectionState);
        }
      };

      this.contributionsChannel?.on('system', {}, handleSystemEvent);
      this.loansChannel?.on('system', {}, handleSystemEvent);
      this.membersChannel?.on('system', {}, handleSystemEvent);
      this.groupsChannel?.on('system', {}, handleSystemEvent);
      this.syncLogsChannel?.on('system', {}, handleSystemEvent);

      this.isSubscribed = true;
      this.connectionState = 'connected';
      this.onConnectionStateChange?.(this.connectionState);
      console.log('✅ Real-time sync subscribed for group:', groupId);
      return true;
    } catch (error) {
      console.error('❌ Failed to subscribe to real-time updates:', error);
      this.connectionState = 'disconnected';
      this.onConnectionStateChange?.(this.connectionState);
      return false;
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): 'connected' | 'disconnected' | 'connecting' {
    return this.connectionState;
  }

  /**
   * Set callback for connection state changes
   */
  setConnectionStateChangeCallback(callback: (state: 'connected' | 'disconnected' | 'connecting') => void) {
    this.onConnectionStateChange = callback;
  }

  /**
   * Unsubscribe from all real-time updates
   */
  async unsubscribe() {
    try {
      await this.contributionsChannel?.unsubscribe();
      await this.loansChannel?.unsubscribe();
      await this.membersChannel?.unsubscribe();
      await this.groupsChannel?.unsubscribe();
      await this.syncLogsChannel?.unsubscribe();

      this.contributionsChannel = null;
      this.loansChannel = null;
      this.membersChannel = null;
      this.groupsChannel = null;
      this.syncLogsChannel = null;

      this.isSubscribed = false;
      this.connectionState = 'disconnected';
      this.onConnectionStateChange?.(this.connectionState);
      console.log('🔌 Real-time sync unsubscribed');
    } catch (error) {
      console.error('❌ Failed to unsubscribe:', error);
      this.connectionState = 'disconnected';
      this.onConnectionStateChange?.(this.connectionState);
    }
  }

  /**
   * Get recent sync logs for admin dashboard
   */
  async getSyncLogs(limit = 50): Promise<SyncLog[]> {
    try {
      const { data, error } = await supabase
        .from('sync_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch sync logs:', error);
      return [];
    }
  }

  /**
   * Get mobile app sync status for a user
   */
  async getMobileSyncStatus(userId: string) {
    try {
      const { data: subscription } = await supabase
        .from('user_subscriptions')
        .select('android_sync_date, access_status')
        .eq('user_id', userId)
        .maybeSingle();

      if (!subscription) return null;

      return {
        lastSync: subscription.android_sync_date,
        accessStatus: subscription.access_status,
        isActive: subscription.access_status === 'active',
      };
    } catch (error) {
      console.error('Failed to get mobile sync status:', error);
      return null;
    }
  }
}

export const realtimeSyncService = new RealtimeSyncService();

